// src/pages/AboutUs.tsx
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import PhilosophyApproach from "../components/PhilosophyApproach";

const PageContainer = styled.div`
    min-height: 100vh;
    background: linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 50%, #C0C0C0 100%);
`;

const Section = styled.section`
    padding: 96px 0;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 1px;
        background: linear-gradient(
                90deg,
                transparent 0%,
                rgba(153, 1, 71, 0.3) 50%,
                transparent 100%
        );
    }
`;

const BackgroundPattern = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.03;
    background-image: radial-gradient(circle at 25% 25%, #990147 2px, transparent 0),
    radial-gradient(circle at 75% 75%, #990147 2px, transparent 0);
    background-size: 60px 60px;
`;

const Container = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
    position: relative;
    z-index: 2;

    @media (max-width: 768px) {
        padding: 0 1rem;
    }
`;

const HeaderWrapper = styled.div`
    text-align: center;
    margin-bottom: 4rem;
`;

const HeaderTag = styled(motion.span)`
    display: inline-block;
    background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
    color: white;
    padding: 0.6rem 1.6rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.5px;
    margin-bottom: 1.2rem;
    box-shadow: 0 6px 20px rgba(153, 1, 71, 0.2);
`;

const SectionTitle = styled(motion.h2)`
    font-size: 2.8rem;
    font-weight: 800;
    background: linear-gradient(135deg, #333333 0%, #990147 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 1.2rem;
    line-height: 1.1;

    @media (max-width: 768px) {
        font-size: 2rem;
    }
`;

const SectionSubtitle = styled(motion.p)`
    font-size: 1rem;
    color: #555555;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.5;
    font-weight: 400;
`;

// Doctor Section
const DoctorGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1.2fr;
    gap: 4rem;
    align-items: start;
    margin: 4rem 0;

    @media (max-width: 968px) {
        grid-template-columns: 1fr;
        gap: 3rem;
    }
`;

const DoctorImageWrapper = styled(motion.div)`
    position: relative;
    height: 600px;
    border-radius: 25px;
    overflow: hidden;
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(255, 255, 255, 0.8) inset;
`;

const DoctorImage = styled(motion.img)`
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
`;

const DoctorInfo = styled.div`
    position: relative;

    h3 {
        font-size: 2.2rem;
        font-weight: 800;
        margin-bottom: 1.5rem;
        background: linear-gradient(135deg, #333333 0%, #990147 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        line-height: 1.1;
    }
`;

const DoctorDescription = styled.div`
    p {
        font-size: 1.1rem;
        color: #555555;
        line-height: 1.7;
        margin-bottom: 1.5rem;
        font-weight: 400;

        &:last-child {
            margin-bottom: 0;
        }
    }
`;

const QualificationsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
    margin: 2.5rem 0;
`;

const QualificationCard = styled(motion.div)`
    background: rgba(255, 255, 255, 0.8);
    border-radius: 12px;
    padding: 1.5rem;
    border-left: 4px solid #990147;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.05);

    .year {
        font-weight: 700;
        color: #990147;
        margin-bottom: 0.5rem;
        font-size: 0.9rem;
    }

    .qualification {
        font-weight: 600;
        color: #333333;
        margin-bottom: 0.5rem;
    }

    .details {
        color: #555555;
        font-size: 0.9rem;
        line-height: 1.4;
    }
`;

// Media Section
const MediaSection = styled.div`
    margin: 4rem 0;
`;

const MediaGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
    margin: 3rem 0;
`;

const MediaCard = styled(motion.div)`
    background: rgba(255, 255, 255, 0.9);
    border-radius: 15px;
    padding: 2rem;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.8);
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);

    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 20px 40px rgba(153, 1, 71, 0.15);
    }
`;

const MediaHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;

    .media-icon {
        width: 40px;
        height: 40px;
        background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 1.2rem;
    }

    .media-title {
        font-size: 1.3rem;
        font-weight: 700;
        background: linear-gradient(135deg, #333333 0%, #990147 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
    }
`;

const MediaContent = styled.div`
    p {
        color: #555555;
        line-height: 1.6;
        margin-bottom: 1rem;
    }
`;

const MediaLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: #990147;
    text-decoration: none;
    font-weight: 600;
    margin-top: 1rem;
    transition: color 0.3s ease;

    &:hover {
        color: #cc3366;
    }
`;

const ServicesFocus = styled(motion.div)`
    background: linear-gradient(135deg, rgba(153, 1, 71, 0.95) 0%, rgba(204, 51, 102, 0.95) 100%);
    color: white;
    padding: 3rem;
    border-radius: 20px;
    margin: 3rem 0;
    box-shadow: 0 20px 40px rgba(153, 1, 71, 0.3);
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        inset: 0;
        background: radial-gradient(
                circle at 20% 80%,
                rgba(255, 255, 255, 0.1) 0%,
                transparent 50%
        );
        pointer-events: none;
    }
`;

const ServicesTitle = styled.h3`
    color: #FFFFFF;
    font-size: 1.8rem;
    font-weight: 700;
    margin-bottom: 2rem;
    text-align: center;
    position: relative;
    z-index: 2;
`;

const ServicesList = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
    position: relative;
    z-index: 2;
`;

const ServiceItem = styled.div`
    display: flex;
    align-items: center;
    gap: 0.8rem;
    color: #FFFFFF;
    font-size: 1rem;
    padding: 0.5rem 0;

    .bullet {
        width: 8px;
        height: 8px;
        background: #FFFFFF;
        border-radius: 50%;
        flex-shrink: 0;
    }
`;

const CTAButton = styled(motion.button)`
    background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
    color: white;
    border: none;
    padding: 1rem 2rem;
    border-radius: 12px;
    font-weight: 700;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-top: 2rem;
    box-shadow: 0 10px 30px rgba(153, 1, 71, 0.3);

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 15px 35px rgba(153, 1, 71, 0.4);
    }
`;

const AboutUs: React.FC = () => {
    const qualifications = [
        {
            year: "1996",
            qualification: "Lekárske štúdium",
            details: "Jesseniovej lekárskej fakulte UK v Martine - ukončené s vyznamenaním"
        },
        {
            year: "2009",
            qualification: "Atestácia v psychiatrii",
            details: "Špecializácia v odbore psychiatria"
        },
        {
            year: "2009",
            qualification: "Kognitívno-behaviorálna psychoterapia",
            details: "4-ročné vzdelávanie, zápis do Zoznamu psychoterapeutov SR"
        },
        {
            year: "2011 - súčasnosť",
            qualification: "Predsedníčka Dozornej rady",
            details: "Gerontopsychiatrickej sekcie Slovenskej psychiatrickej spoločnosti"
        },
        {
            year: "2024",
            qualification: "Súdna znalkyňa",
            details: "Od roku 2024 som zapísaná v zozname znalcov, tlmočníkov a prekladateľov Ministerstva spravodlivosti SR ako znalkyňa pre odbor Zdravotníctvo a farmácia, odvetvie psychiatria."
        }
    ];

    const mediaAppearances = [
        {
            title: "Okná Duše dokorán - RTVS",
            icon: "📺",
            description: "16-dielny cyklus rozprávania a poučných informácií priamo od odborníkov. Originálnym prvkom je práve osoba moderátorky MUDr. Soni Belanskej, ktorá sama je psychiatrička a psychoterapeutka.",
            link: "#"
        },
        {
            title: "Podcast Okná Duše dokorán",
            icon: "🎧",
            description: "Diskusie s ďalším hosťom, odborníkom, získajú ďalší rámec, sú informačne bohaté, no žiadne akademické debaty. Zostáva im akcent na ľudský prístup k pacientovi.",
            link: "#"
        },
        {
            title: "Rozhovor v Týždenníku Téma",
            icon: "📰",
            description: "Obsiahly rozhovor v č. 3/2021 z 15. 1. 2021. Celý rozhovor dostupný na stiahnutie.",
            link: "#"
        },
        {
            title: "Dámsky klub - RTVS",
            icon: "📺",
            description: "Pravidelné vystúpenia v relácii Dámsky klub, diskusie o bipolárnej poruche, vplyve pandémie na duševné zdravie a ďalšie témy.",
            link: "#"
        },
        {
            title: "Supermama - TV JOJ",
            icon: "📺",
            description: "Pravidelný hosť v relácii Supermama, kde poskytuje odborné rady a pohľady na rôzne psychiatrické témy.",
            link: "#"
        },
        {
            title: "Súdna sieň - TV JOJ",
            icon: "⚖️",
            description: "Pravidelné vystupovanie v prípadoch Súdnej siene, ponúka odborný a reálny pohľad na jednotlivé prípady z bohatej klinickej praxe.",
            link: "#"
        }
    ];

    const services = [
        "Úzkostné a depresívne stavy",
        "Rôzne neurózy (panická porucha, fóbie)",
        "Psychosomatické ochorenia",
        "Niektoré typy závislostí",
        "Poruchy príjmu potravy",
        "Poruchy pamäte a demencie",
        "Krízová intervencia",
        "Komunikačné stratégie",
        "Časový manažment",
        "Spánková hygiena",
        "Poradenstvo v oblasti zdravého životného štýlu",
        "Výživové poradenstvo a chudnutie"
    ];

    return (
        <PageContainer>
            <Section id="about-us">
                <BackgroundPattern />
                <Container>
                    <HeaderWrapper>
                        <PhilosophyApproach />
                    </HeaderWrapper>

                    {/* Services Focus */}
                    <ServicesFocus
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        viewport={{ once: true }}
                    >
                        <ServicesTitle>Odborné zameranie ambulancie</ServicesTitle>
                        <ServicesList>
                            {services.map((service, index) => (
                                <ServiceItem key={index}>
                                    <div className="bullet" />
                                    {service}
                                </ServiceItem>
                            ))}
                        </ServicesList>
                    </ServicesFocus>

                    {/* Doctor Section */}
                    <DoctorGrid>
                        <DoctorImageWrapper
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                        >
                            <DoctorImage
                                src="/sonaCennik.jpg"
                                alt="MUDr. Soňa Belanská"
                                initial={{ scale: 1.1 }}
                                whileInView={{ scale: 1 }}
                                transition={{ duration: 1.2 }}
                                viewport={{ once: true }}
                            />
                        </DoctorImageWrapper>

                        <DoctorInfo>
                            <motion.h3
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8 }}
                                viewport={{ once: true }}
                            >
                                MUDr. Soňa Belanská
                            </motion.h3>

                            <DoctorDescription>
                                <motion.p
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.8, delay: 0.1 }}
                                    viewport={{ once: true }}
                                >
                                    Lekárka MUDr. Soňa Belanská je registrovaná s ambulanciou na Bratislavskom VÚC. Postupne pracovala vo viacerých zariadeniach: Psychiatrická nemocnica Veľké Zálužie, Pinelova psychiatrická nemocnica Pezinok, Gerontopsychiatrické odd. PK SZU v Podunajských Biskupiciach, Psychiatrická ambulancia v Nemocnici Milosrdní bratia Bratislava.
                                </motion.p>

                                <motion.p
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.8, delay: 0.2 }}
                                    viewport={{ once: true }}
                                >
                                    Okrem psychiatrie som absolvovala trénerský kurz a som certifikovaný tréner I. a II. kvalifikačného stupňa vo fitnes a kulturistike. Neskôr som na týchto kurzoch vyučovala predmety popisná anatómia a výživa.
                                </motion.p>

                                <QualificationsGrid>
                                    {qualifications.map((qual, index) => (
                                        <QualificationCard
                                            key={index}
                                            initial={{ opacity: 0, y: 30 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                                            viewport={{ once: true }}
                                            whileHover={{ scale: 1.02 }}
                                        >
                                            <div className="year">{qual.year}</div>
                                            <div className="qualification">{qual.qualification}</div>
                                            <div className="details">{qual.details}</div>
                                        </QualificationCard>
                                    ))}
                                </QualificationsGrid>
                            </DoctorDescription>
                        </DoctorInfo>
                    </DoctorGrid>

                    {/* Media Section */}
                    <MediaSection>
                        <HeaderWrapper>
                            <HeaderTag
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                                viewport={{ once: true }}
                            >
                                V Médiách
                            </HeaderTag>
                            <SectionTitle
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                viewport={{ once: true }}
                            >
                                Popularizácia duševného zdravia
                            </SectionTitle>
                            <SectionSubtitle
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                viewport={{ once: true }}
                            >
                                Ako expertku ste ma mohli vidieť vo vysielaní slovenských televíznych staníc alebo počuť v diskusiách Slovenského rozhlasu
                            </SectionSubtitle>
                        </HeaderWrapper>

                        <MediaGrid>
                            {mediaAppearances.map((media, index) => (
                                <MediaCard
                                    key={index}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    whileHover={{ y: -5 }}
                                >
                                    <MediaHeader>
                                        <div className="media-icon">
                                            {media.icon}
                                        </div>
                                        <div className="media-title">{media.title}</div>
                                    </MediaHeader>
                                    <MediaContent>
                                        <p>{media.description}</p>

                                    </MediaContent>
                                </MediaCard>
                            ))}
                        </MediaGrid>
                    </MediaSection>

                </Container>
            </Section>
        </PageContainer>
    );
};

export default AboutUs;